import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const PlayerContext = createContext();
let spotifySdkPromise = null;
const INITIAL_PLAYLIST_CHUNK_SIZE = 20;
const LAZY_QUEUE_BATCH_SIZE = 10;
const LAZY_QUEUE_MIN_BUFFER = 5;
const LAZY_QUEUE_MIN_INTERVAL_MS = 1500;
const LOCAL_LIBRARY_DB_NAME = 'retrofy_local_library';
const LOCAL_LIBRARY_DB_VERSION = 1;
const LOCAL_LIBRARY_STORE_NAME = 'tracks';

function supportsIndexedDb() {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function openLocalLibraryDb() {
  return new Promise((resolve, reject) => {
    if (!supportsIndexedDb()) {
      reject(new Error('IndexedDB unsupported'));
      return;
    }

    const request = window.indexedDB.open(LOCAL_LIBRARY_DB_NAME, LOCAL_LIBRARY_DB_VERSION);
    request.onerror = () => reject(request.error || new Error('Failed to open local library DB'));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCAL_LIBRARY_STORE_NAME)) {
        db.createObjectStore(LOCAL_LIBRARY_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function runIdbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
    request.onsuccess = () => resolve(request.result);
  });
}

async function replaceStoredLocalLibrary(files = []) {
  if (!supportsIndexedDb()) return;
  const db = await openLocalLibraryDb();
  try {
    const transaction = db.transaction(LOCAL_LIBRARY_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(LOCAL_LIBRARY_STORE_NAME);
    await runIdbRequest(store.clear());

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      await runIdbRequest(
        store.put({
          id: `local-file-${index}`,
          index,
          name: String(file?.name || `Track ${index + 1}`),
          type: String(file?.type || ''),
          size: Number(file?.size || 0),
          lastModified: Number(file?.lastModified || 0),
          blob: file,
        })
      );
    }
  } finally {
    db.close();
  }
}

async function readStoredLocalLibrary() {
  if (!supportsIndexedDb()) return [];
  const db = await openLocalLibraryDb();
  try {
    const transaction = db.transaction(LOCAL_LIBRARY_STORE_NAME, 'readonly');
    const store = transaction.objectStore(LOCAL_LIBRARY_STORE_NAME);
    const rows = await runIdbRequest(store.getAll());
    return Array.isArray(rows)
      ? rows.sort((left, right) => Number(left?.index || 0) - Number(right?.index || 0))
      : [];
  } finally {
    db.close();
  }
}

function loadSpotifySdk() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.Spotify?.Player) {
    return Promise.resolve(true);
  }
  if (spotifySdkPromise) {
    return spotifySdkPromise;
  }

  spotifySdkPromise = new Promise((resolve) => {
    let settled = false;
    let timeoutId = null;

    const settle = (value) => {
      if (settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      resolve(value);
    };

    const previousReady = window.onSpotifyWebPlaybackSDKReady;
    window.onSpotifyWebPlaybackSDKReady = () => {
      if (typeof previousReady === 'function') {
        try {
          previousReady();
        } catch {
          // ignore legacy callback issues
        }
      }
      settle(true);
    };

    const existing = document.querySelector('script[data-spotify-sdk="1"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.dataset.spotifySdk = '1';
      script.onerror = () => settle(false);
      document.body.appendChild(script);
    }

    timeoutId = window.setTimeout(() => {
      settle(Boolean(window.Spotify?.Player));
    }, 10000);
  });

  return spotifySdkPromise;
}

export function PlayerProvider({ children, accessToken, onAuthExpired }) {
  const [forceLocalMode, setForceLocalMode] = useState(false);
  const isLocalMode = !accessToken || forceLocalMode;
  const [player, setPlayer] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [playerError, setPlayerError] = useState('');
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(0);
  const [queueTracks, setQueueTracks] = useState([]);
  const playerInstanceRef = useRef(null);
  const lastFetchedTrackIdRef = useRef(null);
  const apiQueueRef = useRef([]);
  const sdkQueueRef = useRef([]);
  const authExpiredNotifiedRef = useRef(false);
  const pendingQueueUrisRef = useRef([]);
  const lazyQueueInFlightRef = useRef(false);
  const lazyQueueLastRunAtRef = useRef(0);
  const localAudioRef = useRef(null);
  const localQueueRef = useRef([]);
  const localIndexRef = useRef(-1);
  const localObjectUrlsRef = useRef([]);
  const localModeRef = useRef(isLocalMode);
  const spotifyReadyRef = useRef(false);

  const notifyAuthExpired = useCallback(() => {
    if (authExpiredNotifiedRef.current) return;
    authExpiredNotifiedRef.current = true;
    if (typeof onAuthExpired === 'function') {
      onAuthExpired();
    }
  }, [onAuthExpired]);

  useEffect(() => {
    localModeRef.current = isLocalMode;
  }, [isLocalMode]);

  useEffect(() => {
    authExpiredNotifiedRef.current = false;
    setPlayerError('');
    pendingQueueUrisRef.current = [];
    if (isLocalMode) {
      setIsReady(true);
    } else if (accessToken) {
      setIsReady(spotifyReadyRef.current);
    }
  }, [accessToken, isLocalMode]);

  const clearLocalObjectUrls = useCallback(() => {
    localObjectUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore URL cleanup errors
      }
    });
    localObjectUrlsRef.current = [];
  }, []);

  const buildLocalTrack = useCallback((fileLike, index) => {
    const objectUrl = URL.createObjectURL(fileLike);
    localObjectUrlsRef.current.push(objectUrl);
    const fallbackName = `Track ${index + 1}`;
    const rawName = String(fileLike?.name || fallbackName);
    const baseName = rawName.replace(/\.[^/.]+$/, '') || fallbackName;
    return {
      id: `local:${rawName}:${Number(fileLike?.size || 0)}:${Number(fileLike?.lastModified || 0)}:${index}`,
      uri: `local:${index}`,
      name: baseName,
      artists: [{ name: 'Local File' }],
      album: { name: 'Local Library', images: [] },
      localSrc: objectUrl,
      duration_ms: 0,
    };
  }, []);

  const syncLocalQueueState = useCallback(() => {
    const index = localIndexRef.current;
    const tracks = localQueueRef.current || [];
    if (index < 0 || index >= tracks.length) {
      setCurrentTrack(null);
      setQueueTracks([]);
      return;
    }
    setCurrentTrack(tracks[index]);
    setQueueTracks(tracks.filter((_track, trackIndex) => trackIndex !== index));
  }, []);

  const playLocalIndex = useCallback(async (index, { autoPlay = true } = {}) => {
    const audio = localAudioRef.current;
    const tracks = localQueueRef.current || [];
    if (!audio || tracks.length === 0 || index < 0 || index >= tracks.length) return false;

    const targetTrack = tracks[index];
    if (!targetTrack?.localSrc) return false;

    localIndexRef.current = index;
    audio.src = targetTrack.localSrc;
    if (typeof targetTrack.volume === 'number') {
      audio.volume = targetTrack.volume;
    }

    syncLocalQueueState();
    setPlaybackPosition(0);
    setPlaybackDuration(0);

    if (!autoPlay) {
      setIsPlaying(false);
      return true;
    }

    try {
      await audio.play();
      setIsPlaying(true);
      return true;
    } catch (error) {
      setIsPlaying(false);
      setPlayerError('Unable to play local file. Check browser permissions and file support.');
      console.error('Error playing local file:', error);
      return false;
    }
  }, [syncLocalQueueState]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.crossOrigin = 'anonymous';
    localAudioRef.current = audio;
    if (isLocalMode) {
      setIsReady(true);
    }
    setPlayerError('');
    setPlayer(null);

    const handleTimeUpdate = () => {
      setPlaybackPosition(Math.round((audio.currentTime || 0) * 1000));
    };
    const handleDurationChange = () => {
      const duration = Number(audio.duration || 0);
      setPlaybackDuration(Number.isFinite(duration) && duration > 0 ? Math.round(duration * 1000) : 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = async () => {
      const tracks = localQueueRef.current || [];
      if (tracks.length === 0) return;
      const nextIndex = localIndexRef.current + 1;
      if (nextIndex < tracks.length) {
        await playLocalIndex(nextIndex, { autoPlay: true });
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    let cancelled = false;
    readStoredLocalLibrary()
      .then(async (rows) => {
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        const tracks = rows
          .map((row, index) => (row?.blob ? buildLocalTrack(row.blob, index) : null))
          .filter(Boolean);
        if (tracks.length === 0) return;
        localQueueRef.current = tracks;
        localIndexRef.current = -1;
        setQueueTracks(tracks);
        setPlayerError('');
        await playLocalIndex(0, { autoPlay: false });
      })
      .catch(() => {
        // ignore local library restore errors
      });

    return () => {
      cancelled = true;
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      localAudioRef.current = null;
      localQueueRef.current = [];
      localIndexRef.current = -1;
      clearLocalObjectUrls();
      setCurrentTrack(null);
      setQueueTracks([]);
      setPlaybackPosition(0);
      setPlaybackDuration(0);
      setIsPlaying(false);
    };
  }, [buildLocalTrack, clearLocalObjectUrls, isLocalMode, playLocalIndex]);

  const normalizeQueueTracks = useCallback((tracks = []) => {
    return (tracks || []).map((track) => ({
      id: track?.id || null,
      uri: track?.uri || null,
      name: track?.name || null,
      artists: Array.isArray(track?.artists) ? track.artists : [],
      album: track?.album || null,
    }));
  }, []);

  const isSameTrack = useCallback((left, right) => {
    if (!left && !right) return true;
    if (!left || !right) return false;
    if ((left.id || null) !== (right.id || null)) return false;
    if ((left.uri || null) !== (right.uri || null)) return false;
    if ((left.name || null) !== (right.name || null)) return false;
    const leftArtistNames = (left.artists || []).map((artist) => artist?.name || '').join('|');
    const rightArtistNames = (right.artists || []).map((artist) => artist?.name || '').join('|');
    return leftArtistNames === rightArtistNames;
  }, []);

  const combineQueues = useCallback((sdkTracks = [], apiTracks = []) => {
    const merged = [];
    const seen = new Set();
    [...sdkTracks, ...apiTracks].forEach((track) => {
      const artistKey = (track?.artists || [])
        .map((artist) => artist?.name || '')
        .filter(Boolean)
        .join('|');
      const keys = [
        track?.uri ? `uri:${track.uri}` : null,
        track?.id ? `id:${track.id}` : null,
        track?.name ? `name:${track.name}::${artistKey}` : null,
      ].filter(Boolean);
      if (keys.length === 0) return;
      if (keys.some((key) => seen.has(key))) return;
      keys.forEach((key) => seen.add(key));
      merged.push(track);
    });
    return merged;
  }, []);

  const normalizeTrack = useCallback((sdkTrack, apiTrack = null) => {
    if (!sdkTrack && !apiTrack) return null;

    const baseTrack = apiTrack || sdkTrack;
    const mergedTrack = {
      id: baseTrack.id || sdkTrack?.id || null,
      uri: baseTrack.uri || sdkTrack?.uri || null,
      name: baseTrack.name || sdkTrack?.name || null,
      artists: baseTrack.artists || sdkTrack?.artists || [],
      album: {
        name: baseTrack.album?.name || sdkTrack?.album?.name || null,
        images: baseTrack.album?.images || sdkTrack?.album?.images || [],
      },
    };

    return mergedTrack;
  }, []);

  const fetchCurrentlyPlaying = useCallback(async () => {
    if (!accessToken) return null;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player/currently-playing', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 204) {
        return null;
      }

      if (response.status === 401) {
        notifyAuthExpired();
        return null;
      }

      if (!response.ok) {
        throw new Error(`currently-playing request failed (${response.status})`);
      }

      const data = await response.json();
      if (!data?.item) {
        return null;
      }

      return {
        track: normalizeTrack(null, data.item),
        isPlaying: Boolean(data.is_playing),
        position: Number(data.progress_ms || 0),
        duration: Number(data.item?.duration_ms || 0),
      };
    } catch (error) {
      console.error('Error fetching currently playing track:', error);
      return null;
    }
  }, [accessToken, normalizeTrack, notifyAuthExpired]);

  const fetchQueue = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player/queue', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 204 || response.status === 404) {
        setQueueTracks([]);
        return;
      }

      if (response.status === 401) {
        notifyAuthExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(`queue request failed (${response.status})`);
      }

      const data = await response.json();
      const queue = Array.isArray(data?.queue) ? data.queue : [];
      apiQueueRef.current = normalizeQueueTracks(queue);
      setQueueTracks(combineQueues(sdkQueueRef.current, apiQueueRef.current));
    } catch (error) {
      console.error('Error fetching Spotify queue:', error);
      setQueueTracks(combineQueues(sdkQueueRef.current, apiQueueRef.current));
    }
  }, [accessToken, normalizeQueueTracks, combineQueues, notifyAuthExpired]);

  useEffect(() => {
    if (!accessToken) return;

    let isMounted = true;
    let createdPlayer = null;

    const initializePlayer = () => {
      try {
        if (playerInstanceRef.current) {
          if (isMounted) setPlayer(playerInstanceRef.current);
          return;
        }

        const newPlayer = new window.Spotify.Player({
          name: 'Retrofy',
          getOAuthToken: (cb) => {
            cb(accessToken);
          },
          volume: 0.7,
        });

        newPlayer.addListener('player_state_changed', (state) => {
          if (localModeRef.current) return;
          if (!state) return;
          if (isMounted) {
            const sdkTrack = state.track_window?.current_track || null;
            const normalizedTrack = normalizeTrack(sdkTrack, null);
            setCurrentTrack((prevTrack) => (isSameTrack(prevTrack, normalizedTrack) ? prevTrack : normalizedTrack));
            setIsPlaying(!state.paused);
            setPlaybackPosition(Number(state.position || 0));
            setPlaybackDuration(Number(state.duration || 0));

            const sdkNextTracks = state.track_window?.next_tracks || [];
            sdkQueueRef.current = normalizeQueueTracks(sdkNextTracks);
            setQueueTracks(combineQueues(sdkQueueRef.current, apiQueueRef.current));

            const trackId = normalizedTrack?.id;
            const shouldEnrichFromApi = trackId && trackId !== lastFetchedTrackIdRef.current;

            if (shouldEnrichFromApi) {
              lastFetchedTrackIdRef.current = trackId;
              fetchCurrentlyPlaying().then((result) => {
                if (!isMounted || !result?.track) return;

                if (result.track.id === trackId) {
                  setCurrentTrack((prevTrack) => (isSameTrack(prevTrack, result.track) ? prevTrack : result.track));
                  setIsPlaying(result.isPlaying);
                  setPlaybackPosition(result.position || 0);
                  setPlaybackDuration(result.duration || 0);
                  fetchQueue();
                }
              });
            }
          }
        });

        newPlayer.addListener('ready', async ({ device_id }) => {
          spotifyReadyRef.current = true;
          if (isMounted) {
            setDeviceId(device_id);
            if (!localModeRef.current) {
              setIsReady(true);
            }
            setPlayerError('');
          }

          try {
            const state = await newPlayer.getCurrentState();
            if (!isMounted || !state) return;

            const sdkTrack = state.track_window?.current_track || null;
            const normalizedTrack = normalizeTrack(sdkTrack, null);
            setCurrentTrack((prevTrack) => (isSameTrack(prevTrack, normalizedTrack) ? prevTrack : normalizedTrack));
            setIsPlaying(!state.paused);
            setPlaybackPosition(Number(state.position || 0));
            setPlaybackDuration(Number(state.duration || 0));
            sdkQueueRef.current = normalizeQueueTracks(state.track_window?.next_tracks || []);
            setQueueTracks(combineQueues(sdkQueueRef.current, apiQueueRef.current));

            const apiResult = await fetchCurrentlyPlaying();
            if (!isMounted || !apiResult?.track) return;

            setCurrentTrack((prevTrack) => (isSameTrack(prevTrack, apiResult.track) ? prevTrack : apiResult.track));
            setIsPlaying(apiResult.isPlaying);
            setPlaybackPosition(apiResult.position || 0);
            setPlaybackDuration(apiResult.duration || 0);
            lastFetchedTrackIdRef.current = apiResult.track.id;
            fetchQueue();
          } catch (error) {
            console.error('Error syncing initial player state:', error);
          }
        });

        newPlayer.addListener('not_ready', ({ device_id }) => {
          spotifyReadyRef.current = false;
          if (isMounted) {
            if (!localModeRef.current) {
              setIsReady(false);
            }
            setPlayerError('Playback device disconnected. Reconnect and try again.');
            setDeviceId((currentDeviceId) => (currentDeviceId === device_id ? null : currentDeviceId));
          }
        });

        newPlayer.addListener('initialization_error', ({ message }) => {
          console.error('Failed to initialize Spotify player:', message);
        });

        newPlayer.addListener('authentication_error', ({ message }) => {
          console.error('Failed to authenticate with Spotify:', message);
        });

        newPlayer.addListener('account_error', ({ message }) => {
          console.error('Spotify account error:', message);
        });

        newPlayer.connect();
        createdPlayer = newPlayer;
        playerInstanceRef.current = newPlayer;
        if (isMounted) setPlayer(newPlayer);
      } catch (error) {
        console.error('Error initializing Spotify player:', error);
      }
    };

    loadSpotifySdk().then((ready) => {
      if (!isMounted || !ready || !window.Spotify?.Player) return;
      initializePlayer();
    });

    return () => {
      isMounted = false;
      if (createdPlayer) {
        createdPlayer.disconnect();
        if (playerInstanceRef.current === createdPlayer) {
          playerInstanceRef.current = null;
        }
      }
    };
  }, [accessToken, fetchCurrentlyPlaying, normalizeTrack, fetchQueue, normalizeQueueTracks, combineQueues, isSameTrack]);

  useEffect(() => {
    if (!accessToken || !isReady || isLocalMode) return undefined;
    fetchQueue();
    const timer = window.setInterval(() => {
      fetchQueue();
    }, 10000);
    return () => window.clearInterval(timer);
  }, [accessToken, isReady, isLocalMode, fetchQueue]);

  // Cleanup player on unmount
  useEffect(() => {
    return () => {
      if (player) {
        player.disconnect();
      }
    };
  }, [player]);

  const activateDevice = useCallback(async (targetDeviceId = deviceId) => {
    if (!accessToken || !targetDeviceId) return false;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_ids: [targetDeviceId],
          play: false,
        }),
      });
      if (response.status === 401) {
        notifyAuthExpired();
        return false;
      }
      return response.ok || response.status === 204;
    } catch (error) {
      console.error('Error activating Spotify device:', error);
      return false;
    }
  }, [accessToken, deviceId, notifyAuthExpired]);

  const recoverPlaybackDevice = useCallback(async () => {
    if (!accessToken) return null;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player/devices', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 401) {
        notifyAuthExpired();
        return null;
      }

      if (!response.ok) return null;

      const data = await response.json();
      const devices = Array.isArray(data?.devices) ? data.devices : [];
      const preferredDevice =
        devices.find((entry) => entry?.id === deviceId) ||
        devices.find((entry) => entry?.name === 'Retrofy') ||
        devices.find((entry) => entry?.is_active) ||
        devices[0];

      if (!preferredDevice?.id) return null;

      const activated = await activateDevice(preferredDevice.id);
      if (!activated) return null;

      setDeviceId(preferredDevice.id);
      setIsReady(true);
      return preferredDevice.id;
    } catch (error) {
      console.error('Error recovering Spotify playback device:', error);
      return null;
    }
  }, [accessToken, activateDevice, deviceId, notifyAuthExpired]);

  const spotifyPlayerRequest = useCallback(
    async (url, options = {}, { activateFirst = false, activateDeviceId = null } = {}) => {
      if (!accessToken) {
        throw new Error('Missing Spotify access token');
      }

      if (activateFirst) {
        await activateDevice(activateDeviceId || deviceId);
      }

      const headers = {
        Authorization: `Bearer ${accessToken}`,
        ...(options.headers || {}),
      };

      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        notifyAuthExpired();
        throw new Error('Spotify auth expired (401)');
      }

      if (!response.ok && response.status !== 204) {
        let details = '';
        try {
          const body = await response.json();
          details = body?.error?.message || body?.error || '';
        } catch {
          details = '';
        }
        const requestError = new Error(`Spotify request failed (${response.status})${details ? `: ${details}` : ''}`);
        requestError.status = response.status;
        requestError.details = details;
        throw requestError;
      }

      return response;
    },
    [accessToken, activateDevice, deviceId, notifyAuthExpired]
  );

  const isDeviceNotFoundError = useCallback((error) => {
    const message = String(error?.message || '').toLowerCase();
    const details = String(error?.details || '').toLowerCase();
    return Number(error?.status) === 404 && (message.includes('device not found') || details.includes('device not found'));
  }, []);

  const withPlaybackDeviceRecovery = useCallback(async (requestFn) => {
    try {
      setPlayerError('');
      await requestFn(deviceId);
      return true;
    } catch (error) {
      if (!isDeviceNotFoundError(error)) {
        if (Number(error?.status) === 401) {
          setPlayerError('Spotify session expired. Reconnect Spotify.');
        }
        throw error;
      }

      setPlayerError('Playback device disconnected. Reconnecting...');
      const recoveredDeviceId = await recoverPlaybackDevice();
      if (!recoveredDeviceId) {
        setPlayerError('Playback device unavailable. Tap Reconnect Player and try again.');
        throw error;
      }

      await requestFn(recoveredDeviceId);
      setPlayerError('');
      return true;
    }
  }, [deviceId, isDeviceNotFoundError, recoverPlaybackDevice]);

  const playTrack = useCallback(
    async (trackUri) => {
      const normalizedUri = String(trackUri || '');
      const isExplicitSpotifyUri = normalizedUri.startsWith('spotify:');
      const isExplicitLocalUri = normalizedUri.startsWith('local:') || normalizedUri.startsWith('local-remote:');
      const shouldUseLocal = isExplicitLocalUri || (!isExplicitSpotifyUri && isLocalMode);
      if (!shouldUseLocal && accessToken) {
        setForceLocalMode(false);
        setIsReady(spotifyReadyRef.current);
      }

      if (shouldUseLocal) {
        const nextIndex = localQueueRef.current.findIndex(
          (track) => track?.uri === trackUri || track?.id === trackUri
        );
        if (nextIndex === -1) return;
        await playLocalIndex(nextIndex, { autoPlay: true });
        return;
      }

      if (!isReady || !deviceId) {
        console.warn('Player not ready');
        setPlayerError('Player not ready. Reconnect and try again.');
        return;
      }

      try {
        pendingQueueUrisRef.current = [];
        await withPlaybackDeviceRecovery(async (targetDeviceId) => {
          await spotifyPlayerRequest(`https://api.spotify.com/v1/me/player/play?device_id=${targetDeviceId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uris: [trackUri],
            }),
          }, { activateFirst: true, activateDeviceId: targetDeviceId });
        });
      } catch (error) {
        console.error('Error playing track:', error);
      }
    },
    [isLocalMode, accessToken, isReady, deviceId, spotifyPlayerRequest, withPlaybackDeviceRecovery, playLocalIndex]
  );

  const playPlaylist = useCallback(
    async (trackUris) => {
      const uris = Array.isArray(trackUris) ? trackUris.filter(Boolean).map((value) => String(value)) : [];
      const hasSpotifyUris = uris.some((uri) => uri.startsWith('spotify:'));
      if (hasSpotifyUris && accessToken) {
        setForceLocalMode(false);
        setIsReady(spotifyReadyRef.current);
      }

      if (!hasSpotifyUris || !accessToken) {
        const localTracks = localQueueRef.current || [];
        if (localTracks.length === 0) {
          setPlayerError('Load local files first to start playback.');
          return;
        }
        await playLocalIndex(0, { autoPlay: true });
        return;
      }

      if (!isReady || !deviceId) {
        console.warn('Player not ready');
        setPlayerError('Player not ready. Reconnect and try again.');
        return;
      }

      try {
        const normalizedUris = [...new Set(uris)];
        if (normalizedUris.length === 0) return;

        const initialUris = normalizedUris.slice(0, INITIAL_PLAYLIST_CHUNK_SIZE);
        pendingQueueUrisRef.current = normalizedUris.slice(INITIAL_PLAYLIST_CHUNK_SIZE);

        await withPlaybackDeviceRecovery(async (targetDeviceId) => {
          await spotifyPlayerRequest(`https://api.spotify.com/v1/me/player/play?device_id=${targetDeviceId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uris: initialUris,
            }),
          }, { activateFirst: true, activateDeviceId: targetDeviceId });
        });
      } catch (error) {
        console.error('Error playing playlist:', error);
      }
    },
    [accessToken, isReady, deviceId, spotifyPlayerRequest, withPlaybackDeviceRecovery, playLocalIndex]
  );

  const enqueuePendingTracks = useCallback(async (targetDeviceId = deviceId, maxCount = LAZY_QUEUE_BATCH_SIZE) => {
    if (!accessToken || !targetDeviceId) return 0;
    if (lazyQueueInFlightRef.current) return 0;
    if (pendingQueueUrisRef.current.length === 0) return 0;

    const now = Date.now();
    if (now - lazyQueueLastRunAtRef.current < LAZY_QUEUE_MIN_INTERVAL_MS) return 0;

    lazyQueueInFlightRef.current = true;
    lazyQueueLastRunAtRef.current = now;

    let enqueued = 0;
    try {
      while (enqueued < maxCount && pendingQueueUrisRef.current.length > 0) {
        const nextUri = pendingQueueUrisRef.current.shift();
        if (!nextUri) continue;

        const queueUrl = `https://api.spotify.com/v1/me/player/queue?uri=${encodeURIComponent(nextUri)}&device_id=${encodeURIComponent(targetDeviceId)}`;
        await spotifyPlayerRequest(queueUrl, { method: 'POST' });
        enqueued += 1;
      }
    } catch (error) {
      if (Number(error?.status) === 429) {
        setPlayerError('Spotify is rate-limiting playback requests. Please wait and try again.');
      }
      console.error('Error lazily enqueuing playlist tracks:', error);
    } finally {
      lazyQueueInFlightRef.current = false;
    }

    return enqueued;
  }, [accessToken, deviceId, spotifyPlayerRequest]);

  useEffect(() => {
    if (!isReady || !deviceId) return;
    if (pendingQueueUrisRef.current.length === 0) return;
    if ((queueTracks || []).length > LAZY_QUEUE_MIN_BUFFER) return;
    enqueuePendingTracks(deviceId, LAZY_QUEUE_BATCH_SIZE);
  }, [isReady, deviceId, queueTracks, enqueuePendingTracks]);

  const togglePlayPause = useCallback(async () => {
    if (isLocalMode) {
      const audio = localAudioRef.current;
      if (!audio) return;
      if (isPlaying) {
        audio.pause();
      } else if (audio.src) {
        try {
          await audio.play();
        } catch (error) {
          console.error('Error resuming local playback:', error);
        }
      } else if ((localQueueRef.current || []).length > 0) {
        await playLocalIndex(Math.max(localIndexRef.current, 0), { autoPlay: true });
      }
      return;
    }

    if (!isReady || !deviceId) return;

    try {
      if (isPlaying) {
        await spotifyPlayerRequest('https://api.spotify.com/v1/me/player/pause', {
          method: 'PUT',
        });
      } else {
        await withPlaybackDeviceRecovery(async (targetDeviceId) => {
          await spotifyPlayerRequest(`https://api.spotify.com/v1/me/player/play?device_id=${targetDeviceId}`, {
            method: 'PUT',
          }, { activateFirst: true, activateDeviceId: targetDeviceId });
        });
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error);
    }
  }, [isLocalMode, isReady, deviceId, isPlaying, spotifyPlayerRequest, withPlaybackDeviceRecovery, playLocalIndex]);

  const nextTrack = useCallback(async () => {
    if (isLocalMode) {
      const tracks = localQueueRef.current || [];
      if (tracks.length === 0) return;
      const nextIndex = localIndexRef.current + 1;
      if (nextIndex >= tracks.length) return;
      await playLocalIndex(nextIndex, { autoPlay: true });
      return;
    }

    if (!isReady || !deviceId) return;

    try {
      await spotifyPlayerRequest('https://api.spotify.com/v1/me/player/next', {
        method: 'POST',
      }, { activateFirst: true });
    } catch (error) {
      console.error('Error skipping to next track:', error);
    }
  }, [isLocalMode, isReady, deviceId, spotifyPlayerRequest, playLocalIndex]);

  const previousTrack = useCallback(async () => {
    if (isLocalMode) {
      const tracks = localQueueRef.current || [];
      if (tracks.length === 0) return;
      const previousIndex = localIndexRef.current - 1;
      if (previousIndex < 0) return;
      await playLocalIndex(previousIndex, { autoPlay: true });
      return;
    }

    if (!isReady || !deviceId) return;

    try {
      await spotifyPlayerRequest('https://api.spotify.com/v1/me/player/previous', {
        method: 'POST',
      }, { activateFirst: true });
    } catch (error) {
      console.error('Error skipping to previous track:', error);
    }
  }, [isLocalMode, isReady, deviceId, spotifyPlayerRequest, playLocalIndex]);

  const setVolume = useCallback(
    async (volumePercent) => {
      if (!isReady) return;
      const clampedPercent = Math.max(0, Math.min(100, Number(volumePercent || 0)));

      if (isLocalMode) {
        const audio = localAudioRef.current;
        if (!audio) return;
        audio.volume = clampedPercent / 100;
        return;
      }

      try {
        if (player && typeof player.setVolume === 'function') {
          await player.setVolume(clampedPercent / 100);
          return;
        }

        if (!deviceId) return;
        await spotifyPlayerRequest(
          `https://api.spotify.com/v1/me/player/volume?volume_percent=${Math.round(clampedPercent)}&device_id=${deviceId}`,
          { method: 'PUT' }
        );
      } catch (error) {
        console.error('Error setting volume:', error);
      }
    },
    [isLocalMode, isReady, player, deviceId, spotifyPlayerRequest]
  );

  const seekToPosition = useCallback(
    async (positionMs) => {
      if (!isReady) return false;
      const nextPosition = Math.max(0, Math.min(Number(positionMs || 0), Number(playbackDuration || 0)));

      if (isLocalMode) {
        const audio = localAudioRef.current;
        if (!audio) return false;
        audio.currentTime = nextPosition / 1000;
        setPlaybackPosition(nextPosition);
        return true;
      }

      if (!deviceId) return false;

      try {
        if (player && typeof player.seek === 'function') {
          await player.seek(nextPosition);
        } else {
          await spotifyPlayerRequest(
            `https://api.spotify.com/v1/me/player/seek?position_ms=${Math.round(nextPosition)}&device_id=${deviceId}`,
            { method: 'PUT' },
            { activateFirst: true }
          );
        }
        setPlaybackPosition(nextPosition);
        return true;
      } catch (error) {
        console.error('Error seeking playback:', error);
        return false;
      }
    },
    [isLocalMode, isReady, deviceId, playbackDuration, player, spotifyPlayerRequest]
  );

  const reconnectPlayer = useCallback(async () => {
    if (isLocalMode) {
      setIsReady(true);
      setPlayerError('');
      return true;
    }
    setPlayerError('Reconnecting playback device...');
    const recoveredDeviceId = await recoverPlaybackDevice();
    if (!recoveredDeviceId) {
      setPlayerError('Could not reconnect device. Open Spotify and try again.');
      return false;
    }
    setPlayerError('');
    return true;
  }, [isLocalMode, recoverPlaybackDevice]);

  const stopPlayback = useCallback(async ({ resetPosition = true } = {}) => {
    if (isLocalMode) {
      const audio = localAudioRef.current;
      if (!audio) return true;
      audio.pause();
      if (resetPosition) {
        audio.currentTime = 0;
        setPlaybackPosition(0);
      }
      setIsPlaying(false);
      return true;
    }

    try {
      if (accessToken && deviceId) {
        await spotifyPlayerRequest('https://api.spotify.com/v1/me/player/pause', {
          method: 'PUT',
        }, { activateFirst: true });
      }
    } catch (error) {
      // Ignore pause errors during source handoff; UI still resets.
      console.error('Error stopping Spotify playback:', error);
    } finally {
      if (resetPosition) {
        setPlaybackPosition(0);
      }
      setIsPlaying(false);
    }
    return true;
  }, [isLocalMode, accessToken, deviceId, spotifyPlayerRequest]);

  const loadLocalFiles = useCallback(async (files) => {
    if (!localAudioRef.current) return false;

    const inputFiles = Array.from(files || []).filter((file) => file instanceof File);
    if (inputFiles.length === 0) return false;

    clearLocalObjectUrls();
    const tracks = inputFiles.map((file, index) => buildLocalTrack(file, index));

    localQueueRef.current = tracks;
    localIndexRef.current = -1;
    setForceLocalMode(true);
    setIsReady(true);
    setQueueTracks(tracks);
    setPlayerError('');
    await playLocalIndex(0, { autoPlay: true });
    replaceStoredLocalLibrary(inputFiles).catch(() => {
      // ignore persistence errors and keep in-memory playback
    });
    return true;
  }, [buildLocalTrack, clearLocalObjectUrls, playLocalIndex]);

  const loadLocalRemoteTracks = useCallback(async (entries, startIndex = 0) => {
    if (!localAudioRef.current) return false;
    const normalized = Array.from(entries || []).filter((entry) => Boolean(entry?.url));
    if (normalized.length === 0) return false;

    clearLocalObjectUrls();
    const tracks = normalized.map((entry, index) => ({
      id: `local-remote:${index}:${entry.url}`,
      uri: `local-remote:${index}`,
      name: String(entry.name || `Track ${index + 1}`),
      artists: [{ name: 'Local Library' }],
      album: { name: 'Local Media', images: [] },
      localSrc: String(entry.url),
      duration_ms: 0,
    }));

    localQueueRef.current = tracks;
    localIndexRef.current = -1;
    setForceLocalMode(true);
    setIsReady(true);
    setQueueTracks(tracks);
    setPlayerError('');
    const safeStartIndex = Math.max(0, Math.min(Number(startIndex || 0), tracks.length - 1));
    await playLocalIndex(safeStartIndex, { autoPlay: true });
    return true;
  }, [clearLocalObjectUrls, playLocalIndex]);

  const persistLocalQueueIfPossible = useCallback((tracks = []) => {
    const normalized = Array.isArray(tracks) ? tracks : [];
    if (normalized.length === 0) {
      replaceStoredLocalLibrary([]).catch(() => undefined);
      return;
    }
    const allFileBacked = normalized.every((track) => track?.localFileBlob instanceof Blob);
    if (!allFileBacked) return;

    const files = normalized.map((track, index) => {
      const blob = track.localFileBlob;
      if (blob instanceof File) return blob;
      const fallbackName = String(track?.localFilename || `${track?.name || `Track ${index + 1}`}.mp3`);
      return new File([blob], fallbackName, {
        type: blob.type || 'audio/mpeg',
        lastModified: Date.now(),
      });
    });
    replaceStoredLocalLibrary(files).catch(() => undefined);
  }, []);

  const clearLocalLibrary = useCallback(async () => {
    const audio = localAudioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }

    localQueueRef.current = [];
    localIndexRef.current = -1;
    clearLocalObjectUrls();
    setCurrentTrack(null);
    setQueueTracks([]);
    setPlaybackPosition(0);
    setPlaybackDuration(0);
    setIsPlaying(false);
    setPlayerError('');
    persistLocalQueueIfPossible([]);

    if (accessToken) {
      setForceLocalMode(false);
      setIsReady(spotifyReadyRef.current);
    } else {
      setIsReady(true);
    }
    return true;
  }, [accessToken, clearLocalObjectUrls, persistLocalQueueIfPossible]);

  const removeLocalTrack = useCallback(async (trackId) => {
    const tracks = [...(localQueueRef.current || [])];
    if (tracks.length === 0) return false;

    const removeIndex = tracks.findIndex((track) => track?.id === trackId || track?.uri === trackId);
    if (removeIndex < 0) return false;

    const isCurrentTrack = localIndexRef.current === removeIndex;
    const wasPlaying = isPlaying;

    tracks.splice(removeIndex, 1);
    if (tracks.length === 0) {
      await clearLocalLibrary();
      return true;
    }

    const previousIndex = localIndexRef.current;
    let nextIndex = previousIndex;
    if (removeIndex < previousIndex) {
      nextIndex = previousIndex - 1;
    } else if (isCurrentTrack) {
      nextIndex = Math.min(removeIndex, tracks.length - 1);
    }
    if (nextIndex < 0) nextIndex = 0;

    localQueueRef.current = tracks;
    localIndexRef.current = nextIndex;
    persistLocalQueueIfPossible(tracks);

    if (isCurrentTrack) {
      await playLocalIndex(nextIndex, { autoPlay: wasPlaying });
    } else {
      syncLocalQueueState();
    }
    return true;
  }, [clearLocalLibrary, isPlaying, persistLocalQueueIfPossible, playLocalIndex, syncLocalQueueState]);

  const value = {
    isReady,
    playerError,
    currentTrack,
    isPlaying,
    playbackPosition,
    playbackDuration,
    queueTracks,
    playTrack,
    playPlaylist,
    togglePlayPause,
    nextTrack,
    previousTrack,
    setVolume,
    seekToPosition,
    reconnectPlayer,
    stopPlayback,
    loadLocalFiles,
    loadLocalRemoteTracks,
    removeLocalTrack,
    clearLocalLibrary,
    isLocalMode,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
}
